import { createTheme, responsiveFontSizes } from '@mui/material/styles'

// COLOR USAGE GUIDELINES
// Primary: Main brand color - CTAs, links, active states (Fresh mint green)
// Secondary: Accent color - secondary actions, AI features, highlights (Teal/turquoise)
// Success/Error/Warning/Info: Semantic feedback only
// background.default: Page background (white)
// background.paper: Cards, dialogs, elevated surfaces (light gray)
// background.dark: Dark hero sections, dramatic content
// grey[50-900]: Borders, dividers, disabled states, subtle backgrounds
// text.primary/secondary/disabled: All text content

// ============================================================================
// OPTION 1: FRESH & MODERN (ACTIVE)
// Vibrant mint-green-to-teal palette emphasizing the light green
// ============================================================================
const globalTheme = createTheme({
  palette: {
    // Brand colors
    primary: {
      main: '#2AB574', // Darker mint green for better contrast (was #3DD68C)
      light: '#76F4AC', // Light green - USER FAVORITE! Use for backgrounds/accents, NOT text
      dark: '#1a4d3a', // Deep forest green - excellent for text on white
      contrastText: '#FFF',
    },
    secondary: {
      main: '#8B4A9C', // Teal/turquoise (was #8B4A9C purple)
      light: '#A8E6CF', // Soft mint (was #EFE8DF beige)
      dark: '#008B74', // Darker teal (was #160019 dark purple)
      contrastText: '#FFF'
    },
    
    // Semantic colors for status feedback
    success: {
      main: '#4caf50',
      light: '#d4edda', 
      dark: '#155724'
    },
    error: {
      main: '#f44336',
      light: '#f8d7da',
      dark: '#721c24'
    },
    warning: {
      main: '#ff9800',
      light: '#fff3cd',
      dark: '#856404'
    },
    info: {
      main: '#2196f3',
      light: '#f0f8ff',
      dark: '#0d47a1'
    },
    
    // Background colors
    background: {
      default: '#FFFFFF', // Main page background
      paper: '#F5F7F9', // Elevated surfaces (cards, dialogs)
      dark: '#1a1a2e' // Dark hero sections & filters (keep original)
    },
    
    // AI-specific palette (purple identity — used only by AI components)
    ai: {
      main: '#7E57C2',
      light: '#D1C4E9',
      dark: '#4A2D73',
      contrastText: '#FFF',
      shadow: '0 8px 30px rgba(126, 87, 194, 0.3)',
    },

    // Custom shadows
    shadows: {
      light: '0 2px 8px rgba(0,0,0,0.08)',
      medium: '0 4px 20px rgba(0,0,0,0.12)',
      heavy: '0 8px 32px rgba(0,0,0,0.16)',
      colored: {
        primary: '0 6px 20px rgba(42, 181, 116, 0.4)', // Updated for darker mint green
        primaryHover: '0 8px 25px rgba(42, 181, 116, 0.6)', // Updated for darker mint green
        secondary: '0 8px 30px rgba(0, 201, 167, 0.3)' // Teal shadow
      }
    }
  },
})

// ============================================================================
// COLOR USAGE BEST PRACTICES FOR ACCESSIBILITY
// ============================================================================
// ✅ FOR TEXT on white backgrounds: Use primary.dark (#1a4d3a) or secondary.dark (#008B74)
// ✅ FOR BUTTONS/CTAs: Use primary.main (#2AB574) background with white text
// ✅ FOR BACKGROUNDS/ACCENTS: Use primary.light (#76F4AC) - NEVER for text
// ✅ FOR HOVER STATES: Use primary.light (#76F4AC) backgrounds
// 
// CONTRAST RATIOS (WCAG AA requires 4.5:1 for normal text, 3:1 for large text):
// - primary.main (#2AB574) on background.dark (#1a1a2e): ~6.2:1 ✓ Passes WCAG AA
// - primary.dark (#1a4d3a) on white: ~7.8:1 ✓ Passes WCAG AAA
// ============================================================================

// ============================================================================
// OPTION 2: SOFT & CALMING (BACKUP - UNCOMMENT TO USE)
// Makes the light green the primary color for a softer, more modern feel
// NOTE: Be careful with contrast if using light green as primary.main
// ============================================================================
/*
const globalTheme = createTheme({
  palette: {
    primary: {
      main: '#2AB574', // Use darker green for better contrast (not #76F4AC)
      light: '#76F4AC', // Light green for accents only
      dark: '#2d5940', // Muted forest green
      contrastText: '#FFF',
    },
    secondary: {
      main: '#4ECDC4', // Bright turquoise
      light: '#B8E6E3', // Soft aqua
      dark: '#2a7b76', // Deep teal
      contrastText: '#FFF'
    },
    
    // Keep all other colors the same as Option 1
    success: { main: '#4caf50', light: '#d4edda', dark: '#155724' },
    error: { main: '#f44336', light: '#f8d7da', dark: '#721c24' },
    warning: { main: '#ff9800', light: '#fff3cd', dark: '#856404' },
    info: { main: '#2196f3', light: '#f0f8ff', dark: '#0d47a1' },
    background: { default: '#FFFFFF', paper: '#F5F7F9', dark: '#1a1a2e' },
    shadows: {
      light: '0 2px 8px rgba(0,0,0,0.08)',
      medium: '0 4px 20px rgba(0,0,0,0.12)', 
      heavy: '0 8px 32px rgba(0,0,0,0.16)',
      colored: {
        primary: '0 6px 20px rgba(42, 181, 116, 0.4)',
        primaryHover: '0 8px 25px rgba(42, 181, 116, 0.6)',
        secondary: '0 8px 30px rgba(78, 205, 196, 0.3)'
      }
    }
  },
})
*/

let theme = createTheme(
  {
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '@global': {
            html: {
              fontFamily: `"Kumbh Sans", sans-serif`,
            },
            body: {
              fontFamily: `"Kumbh Sans", sans-serif`,
            },
            '*': {
              fontFamily: `"Kumbh Sans", sans-serif !important`,
            },
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            fontFamily: `"Kumbh Sans", sans-serif`,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            fontFamily: `"Kumbh Sans", sans-serif`,
          },
          outlined: {
            // Use darker green for better contrast on white backgrounds
            color: globalTheme.palette.primary.dark,
            borderColor: globalTheme.palette.primary.main,
            '&:hover': {
              borderColor: globalTheme.palette.primary.dark,
              backgroundColor: 'rgba(26, 77, 58, 0.04)', // Light tint of primary.dark
            },
          },
          outlinedSecondary: {
            color: globalTheme.palette.secondary.dark,
            borderColor: globalTheme.palette.secondary.main,
            '&:hover': {
              borderColor: globalTheme.palette.secondary.dark,
              backgroundColor: 'rgba(0, 139, 116, 0.04)', // Light tint of secondary.dark
            },
          },
        },
        variants: [
          {
            props: { stylevariant: 'header', variant: 'contained' },
            style: {
              color: globalTheme.palette.primary.contrastText,
              backgroundColor: globalTheme.palette.primary.dark,
            },
          },
          {
            props: (props) => props.stylevariant === 'header' && props.variant !== 'contained',
            style: {
              color: globalTheme.palette.primary.dark,
            },
          },
        ],
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: globalTheme.palette.background.paper,
            color: globalTheme.palette.primary.dark,
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            height: '4px',
            borderRadius: '8px',
            backgroundColor: globalTheme.palette.secondary.main,
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            backgroundColor: globalTheme.palette.common.white,
            color: globalTheme.palette.primary.dark,
            fontSize: '1rem',
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: globalTheme.palette.primary.dark,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: globalTheme.palette.common.white,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              backgroundColor: globalTheme.palette.common.white,
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            // Controls default (unchecked) color for the thumb
            color: globalTheme.palette.grey[600],
            '&.Mui-checked': {
              // Controls checked color for the thumb
              color: globalTheme.palette.primary.main,
            },
          },
          track: {
            // Controls default (unchecked) color for the track
            opacity: 1,
            backgroundColor: globalTheme.palette.grey[400],
            '.Mui-checked.Mui-checked + &': {
              // Controls checked color for the track
              opacity: 1,
              backgroundColor: globalTheme.palette.primary.light,
            },
          },
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          root: {
            color: globalTheme.palette.grey[700],
            '&.Mui-selected': {
              backgroundColor: globalTheme.palette.primary.main,
              '&:hover': {
                backgroundColor: globalTheme.palette.primary.main,
              },
            },
            '&.MuiPickersDay-today': {
              color: globalTheme.palette.primary.main,
              border: `1px solid ${globalTheme.palette.primary.main}`,
            },
          },
        },
      },
      CKEditor: {
        styleOverrides: {
          editor: {
            height: '1000vh',
            overflow: 'auto',
            border: `1px solid ${globalTheme.palette.grey[900]}`,
          },
        },
      },
    },
    color: props => (props.inverted ? globalTheme.palette.primary.dark : globalTheme.palette.common.white),
    typography: {
      fontFamily: `"Kumbh Sans", sans-serif`,
      color: props => (props.inverted ? globalTheme.palette.primary.dark : globalTheme.palette.common.white),
      h1: {
        fontFamily: `"Kumbh Sans", sans-serif`,
        fontSize: '2.5rem',
        '@media (max-width:600px)': {
          fontSize: '2rem',
        },
        color: props => (props.inverted ? globalTheme.palette.primary.dark : globalTheme.palette.grey[900]),
      },
      h2: {
        fontFamily: `"Kumbh Sans", sans-serif`,
        fontSize: '2rem',
        '@media (max-width:600px)': {
          fontSize: '1.5rem',
        },
        color: globalTheme.palette.primary.dark,
      },
      h3: {
        fontFamily: `"Kumbh Sans", sans-serif`,
        color: props =>
          props.inverted && !props.emphasis
            ? globalTheme.palette.common.white
            : props.emphasis
              ? globalTheme.palette.warning.main
              : globalTheme.palette.primary.dark,
      },
      h4: {
        fontFamily: `"Kumbh Sans", sans-serif`,
        color: globalTheme.palette.primary.dark,
      },
      h5: {
        fontFamily: `"Kumbh Sans", sans-serif`,
        color: props => (props.inverted ? globalTheme.palette.primary.dark : globalTheme.palette.grey[900]),
      },

      // LOGO Font
      h6: {
        fontFamily: `"Kumbh Sans", sans-serif`,
        color: globalTheme.palette.primary.dark,
      },
      body1: {
        fontFamily: `"Kumbh Sans", sans-serif`,
        fontSize: '1rem',
        lineHeight: 1.6,
        '@media (max-width:600px)': {
          fontSize: '0.9375rem',
          lineHeight: 1.5,
        },
        color: props => (props.inverted ? globalTheme.palette.common.white : globalTheme.palette.primary.dark),
      },
      body2: {
        fontFamily: `"Kumbh Sans", sans-serif`,
        fontSize: '0.875rem',
        lineHeight: 1.5,
        '@media (max-width:600px)': {
          fontSize: '0.8125rem',
          lineHeight: 1.4,
        },
        color: props => (props.inverted ? globalTheme.palette.common.white : globalTheme.palette.primary.dark),
      },
      subtitle1: {
        fontFamily: `"Kumbh Sans", sans-serif`,
        color: props => (props.inverted ? globalTheme.palette.common.white : globalTheme.palette.primary.dark),
      },
      subtitle2: {
        fontFamily: `"Kumbh Sans", sans-serif`,
        color: props => (props.inverted ? globalTheme.palette.secondary.dark : globalTheme.palette.common.white),
        fontWeight: props => (props.inverted ? 'bold' : 400),
      },
      caption: {
        fontFamily: `"Kumbh Sans", sans-serif`,
        fontStyle: 'italic',
        color: props => (props.inverted ? globalTheme.palette.common.white : globalTheme.palette.primary.dark),
      },
      overline: {
        fontFamily: `"Kumbh Sans", sans-serif`,
        color: props => (props.inverted ? globalTheme.palette.common.white : globalTheme.palette.primary.dark),
      },
    },
    layout: {
      toolbarHeight: 64, // Height of the top toolbar
      contentPadding: 0, // Padding around inner content
      tableContentPadding: 8, // Padding around inner content for tables
      footerHeight: 80, // Height of the footer
      tableFilterBarHeight: 56, // The height of the table filter selector
      tableFooterHeight: 20, // The height of the table footer
      contentRowSpacing: 2, // Space between horizontal content rows, for table height calculations
      multiSelectBarHeight: 56, //The height of the table's multi select toolbar
      progressSize: 24, // size of the circular progress indicator shown when dialogs are submitting
      progressSizeLarge: 80, // size of the large circular progress indicator shown when dialogs are submitting
      backgroundColor: globalTheme.palette.common.white, // Background color of the viewport
      backdropColor: globalTheme.palette.common.white, // Background color behind the viewport (seen if the viewport has padding or margins).
      mobileLRPadding: 24, // Left and Right padding on mobile
    },
  },
  globalTheme,
)

theme = responsiveFontSizes(theme)
export default theme
